import type { InterruptionHandlers, NowPlayingInfo, RemoteHandlers } from './musicSystem';

/**
 * Браузерная половина `musicSystem.ts` — Media Session API: название и обложка в системной
 * плашке браузера и на экране блокировки телефона, медиаклавиши клавиатуры и гарнитуры.
 */
export type { NowPlayingInfo, RemoteHandlers };

function session(): MediaSession | null {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : null;
}

export async function showNowPlaying(info: NowPlayingInfo): Promise<void> {
  const s = session();
  if (!s) {
    return;
  }
  s.metadata = new MediaMetadata({
    title: info.title,
    artist: info.artist,
    album: info.album,
    artwork: info.artworkPath ? [{ src: info.artworkPath, sizes: '1000x1000' }] : [],
  });
  s.playbackState = info.playing ? 'playing' : 'paused';
  if (info.durationSec > 0 && 'setPositionState' in s) {
    try {
      s.setPositionState({
        duration: info.durationSec,
        position: Math.min(info.elapsedSec, info.durationSec),
        playbackRate: 1,
      });
    } catch {
      // Позиция за пределами длительности (длительность ещё уточняется) — не страшно.
    }
  }
}

export async function hideNowPlaying(): Promise<void> {
  const s = session();
  if (s) {
    s.metadata = null;
    s.playbackState = 'none';
  }
}

export function subscribeRemote(h: RemoteHandlers): () => void {
  const s = session();
  if (!s) {
    return () => undefined;
  }
  const actions: [MediaSessionAction, MediaSessionActionHandler][] = [
    ['play', () => h.play()],
    ['pause', () => h.pause()],
    ['nexttrack', () => h.next()],
    ['previoustrack', () => h.previous()],
    ['seekto', (d) => (d.seekTime !== undefined ? h.seekTo(d.seekTime) : undefined)],
    ['stop', () => h.stop()],
  ];
  for (const [a, fn] of actions) {
    try {
      s.setActionHandler(a, fn);
    } catch {
      // Действие не поддерживается этим браузером.
    }
  }
  return () => {
    for (const [a] of actions) {
      try {
        s.setActionHandler(a, null);
      } catch {
        // То же.
      }
    }
  };
}

/** В браузере прерывания и наушники обрабатывает сам браузер: он ставит `<audio>` на паузу. */
export function subscribeInterruptions(_h: InterruptionHandlers): () => void {
  return () => undefined;
}

/** Аудиофокусом в браузере распоряжается сам браузер. */
export function acquireAudioFocus(): void {}

export function releaseAudioFocus(): void {}
